import React, { useEffect, useRef, useState } from "react";
import { FileUp, Paperclip, X } from "lucide-react";
import { FormError } from "../ui/feedback";
import { FlowpiDialogSurface } from "../ui/flowpi-dialog";
import { Button } from "../ui/button";
import { formatApiError } from "../../lib/api";
import {
  getQuotationAttachment,
  putQuotationAttachment,
} from "../../lib/quotationApi";

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

export async function validateQuotationAttachmentFile(file) {
  if (!file || file.size === 0) return "Select a non-empty PDF file.";
  if (file.size > MAX_ATTACHMENT_BYTES) return "PDF attachment must not exceed 10 MB.";
  if (file.type !== "application/pdf") return "Only PDF attachments are accepted.";
  const signature = new Uint8Array(await file.slice(0, 5).arrayBuffer());
  if (String.fromCharCode(...signature) !== "%PDF-") {
    return "The selected file does not have a valid PDF signature.";
  }
  return "";
}

function formatSize(value) {
  if (!Number.isFinite(value)) return "—";
  if (value < 1024) return `${value} B`;
  return `${(value / 1024 / 1024).toFixed(2)} MB`;
}

function formatTimestamp(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleString("id-ID");
}

export default function QuotationAttachmentControl({ isAdmin }) {
  const [attachment, setAttachment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const requestInFlight = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    getQuotationAttachment({ signal: controller.signal })
      .then((value) => {
        setAttachment(value);
        setLoadError("");
      })
      .catch((requestError) => {
        if (requestError?.code !== "ERR_CANCELED") {
          setLoadError(formatApiError(requestError));
        }
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  const close = () => {
    if (submitting) return;
    setOpen(false);
    setFile(null);
    setConfirming(false);
    setError("");
  };

  const continueUpload = async () => {
    const validation = await validateQuotationAttachmentFile(file);
    if (validation) {
      setError(validation);
      return;
    }
    setError("");
    if (attachment) {
      setConfirming(true);
      return;
    }
    await submit();
  };

  const submit = async () => {
    if (requestInFlight.current) return;
    const validation = await validateQuotationAttachmentFile(file);
    if (validation) {
      setError(validation);
      return;
    }
    requestInFlight.current = true;
    setSubmitting(true);
    setError("");
    try {
      const saved = await putQuotationAttachment(file);
      setAttachment(saved);
      setSuccess(attachment ? "Attachment replaced successfully." : "Attachment uploaded successfully.");
      setOpen(false);
      setFile(null);
      setConfirming(false);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      requestInFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2" data-testid="quotation-attachment-status">
        <div className="max-w-64 text-right text-xs text-muted-foreground">
          {loading ? "Loading attachment…" : loadError ? (
            <span className="text-destructive">{loadError}</span>
          ) : attachment ? (
            <>
              <span className="block truncate font-medium text-foreground" title={attachment.filename}>{attachment.filename}</span>
              <span>{formatSize(attachment.size_bytes)} · {formatTimestamp(attachment.uploaded_at)}</span>
            </>
          ) : "No global attachment"}
          {success ? <span className="block text-success">{success}</span> : null}
        </div>
        {isAdmin ? (
          <Button variant="edit" onClick={() => setOpen(true)} disabled={loading} data-testid="quotation-attachment-action">
            <Paperclip />{attachment ? "Replace Attachment" : "Upload Attachment"}
          </Button>
        ) : null}
      </div>
      {open ? (
        <FlowpiDialogSurface
          open
          onClose={close}
          busy={submitting}
          accessibleTitle={attachment ? "Replace Attachment" : "Upload Attachment"}
          size="form"
          testId="quotation-attachment-dialog"
        >
          <div className="w-full max-w-lg overflow-hidden rounded-lg border bg-card shadow-2xl">
            <header className="flex items-start justify-between border-b px-5 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">Global PDF</p>
                <h2 id="quotation-attachment-title" className="text-xl font-semibold">
                  {attachment ? "Replace Attachment" : "Upload Attachment"}
                </h2>
              </div>
              <Button variant="ghost" size="icon" onClick={close} disabled={submitting} aria-label="Close attachment dialog"><X /></Button>
            </header>
            <div className="space-y-4 px-5 py-5">
              {confirming ? (
                <div className="rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm">
                  Replace <b>{attachment.filename}</b> with <b>{file?.name}</b>? The previous file will be permanently removed.
                </div>
              ) : (
                <label className="block">
                  <span className="mb-2 block text-sm font-medium">PDF attachment (maximum 10 MB)</span>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    className="wms-input w-full"
                    disabled={submitting}
                    onChange={(event) => {
                      setFile(event.target.files?.[0] || null);
                      setError("");
                    }}
                  />
                </label>
              )}
              <FormError>{error}</FormError>
            </div>
            <footer className="flex justify-end gap-2 border-t px-5 py-4">
              <Button variant="cancel" onClick={close} disabled={submitting}>Cancel</Button>
              <Button
                variant="export"
                onClick={confirming ? submit : continueUpload}
                disabled={!file}
                loading={submitting}
                loadingLabel="Uploading…"
              >
                <FileUp />{confirming ? "Confirm Replace" : attachment ? "Continue" : "Upload"}
              </Button>
            </footer>
          </div>
        </FlowpiDialogSurface>
      ) : null}
    </>
  );
}
